// Browser-native print-to-PDF exporter. Replaces the html2canvas+jsPDF
// rasterization pipeline with the browser's own print rendering engine.
//
// Why: html2canvas-pro has chronic bugs with inline elements that wrap across
// line boundaries (background-color spans drop glyphs, marker baselines drift,
// bold font fallbacks fail). No amount of CSS overrides reliably fix these
// because we don't control the rasterizer. Browser-native print rendering uses
// the SAME engine that paints the editor — so what you see is what you get,
// modulo the print stylesheet's intentional changes (page size, break rules).
//
// How it works:
//   1. Create a hidden iframe and copy every <style>/<link> from the host
//      document into it, so CSS-module class names + Plate's own SCSS resolve.
//   2. Inject a print stylesheet — page size, margins, break rules for
//      headings/tables/figures, color preservation.
//   3. Clone the editor's content area (only the slate root, not the chrome)
//      into the iframe's <body>.
//   4. Wait for fonts + images.
//   5. Call iframe.contentWindow.print() — the browser shows its print dialog
//      with "Save as PDF" pre-selected as a destination on every modern
//      browser (Chrome/Edge/Safari/Firefox).
//
// Pagination: declarative via CSS print rules instead of imperative JS:
//   - headings stick to their next block (break-after: avoid)
//   - tables / table rows never split across pages (break-inside: avoid)
//   - figures, images, blockquotes stay whole
//
// What we lose: the singlePage tall-page mode of the old exporter. Print CSS
// can fake it via @page { size: 8in 50in } but real-world viewers cap page
// height at ~200in. If users still want that mode we can re-add a code path,
// but it's outside the standard "Export .pdf" UX.

import { waitForImages, stripInteractiveChrome, normalizeTableColumnsForPrint, trimTrailingEmptyBlocks } from './exportHelpers';

interface ExportToPdfOptions {
    target: HTMLElement;
    filename?: string;
}

// Collect every stylesheet element from the host document so the iframe
// renders with identical styles. CSS Module classnames resolve through the
// existing <style> tags React injected at build time.
function collectHostStyles(): string {
    const nodes = document.querySelectorAll('style, link[rel="stylesheet"]');
    return Array.from(nodes)
        .map((n) => (n as HTMLElement).outerHTML)
        .join('\n');
}

// Brand font families used by the editor (see src/scss/index.scss @font-face
// rules). The export must paint with the bundled web fonts on EVERY machine —
// not whatever the reader happens to have installed — so we force-load each
// weight in the iframe before printing instead of trusting the passive
// fonts.ready (which resolves instantly on a freshly-written iframe, before any
// .ttf fetch has even been requested — the print() then races the download and
// falls back to a system font on machines without Gilroy installed).
const GILROY_FACES: Array<{ weight: number; family: string }> = [
    { weight: 300, family: 'Gilroy-Light' },
    { weight: 400, family: 'Gilroy-Medium' },
    { weight: 500, family: 'Gilroy-Regular' },
    { weight: 600, family: 'Gilroy-SemiBold' },
    { weight: 700, family: 'Gilroy-Bold' },
    { weight: 800, family: 'Gilroy-ExtraBold' },
];

// Guarantee the iframe paints with the same fonts as the live editor,
// independent of the reader's locally-installed fonts. Two belts:
//   1. Adopt the parent document's already-decoded FontFace objects — the live
//      editor renders in the parent, so its fonts are guaranteed loaded.
//   2. Explicitly force-load each Gilroy weight via the iframe's own copied
//      @font-face rules, so fonts.ready has a real pending load to await even
//      where cross-document FontFace adoption is unsupported.
// Bounded by `timeoutMs` so a single stuck font fetch can never hang the export.
async function ensureFontsLoaded(doc: Document, timeoutMs = 4000): Promise<void> {
    const childSet: any = (doc as any).fonts;
    if (!childSet) return;

    // (1) Adopt parent faces (best-effort — some engines reject cross-doc add()).
    try {
        (document as any).fonts?.forEach((face: FontFace) => {
            try {
                childSet.add(face);
            } catch {
                /* cross-document add unsupported — force-load below covers it */
            }
        });
    } catch {
        /* FontFaceSet not iterable — fall through to force-load */
    }

    // (2) Force-load each brand weight so a pending load exists for ready.
    const loads = GILROY_FACES.map(({ weight, family }) => childSet.load?.(`${weight} 16px "${family}"`)?.catch?.(() => undefined)).filter(
        Boolean,
    );

    const ready = (async () => {
        try {
            await Promise.all(loads);
            await childSet.ready;
        } catch {
            /* best-effort */
        }
    })();

    await Promise.race([ready, new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))]);
}

// Print stylesheet — pagination + chrome stripping + color preservation. The
// rules here are the declarative equivalent of the old planPages JS logic:
//   * break-after: avoid on headings = heading + next block stay together
//   * break-inside: avoid on tables/rows = no row splits across pages
//   * print-color-adjust: exact = backgrounds + highlights print as-is
//     (browsers strip backgrounds by default on print to save ink — we want
//     the highlights + cell tints to come through)
const PRINT_CSS = `
    @page {
        size: A3 portrait;
        /* margin: 0 removes the @page margin area where browsers inject their
           URL/title header and footer decorations. Visual page spacing is
           provided instead via body margin below — the end result is identical
           but without the browser-added chrome. */
        margin: 0;
    }

    @media print {
        html {
            margin: 0;
            padding: 0;
        }

        body {
            /* Equivalent to the former @page margins — 0.75in sides,
               0.75in top, 0.85in bottom — now as body margin so the
               browser has nowhere to render its URL/title decorations. */
            margin: 0.75in 0.75in 0.85in 0.75in;
            padding: 0;
            background: #ffffff;
        }

        /* Force the editor content to render at print page width without
           any scroll containers or max-width restrictions. */
        body > * {
            max-width: none !important;
            box-shadow: none !important;
            border: none !important;
            border-radius: 0 !important;
            background: #ffffff !important;
        }

        /* Strip any editor chrome that snuck in via the clone (resize handles,
           floating toolbars, ghost carets, selection highlights). */
        [data-plate-toolbar],
        [data-floating-toolbar],
        [contenteditable] [data-slate-placeholder],
        [data-resize-handle],
        button,
        .__resize-handle,
        [class*="ResizeHandle"],
        [class*="FloatingToolbar"],
        [class*="toolbarHost"] {
            display: none !important;
        }

        /* Pagination — declarative replacements for planPages. */
        h1, h2, h3, h4, h5, h6 {
            break-after: avoid;
            page-break-after: avoid;
            break-inside: avoid;
            page-break-inside: avoid;
        }

        table {
            break-inside: auto;
            page-break-inside: auto;
        }

        tr, td, th {
            break-inside: avoid;
            page-break-inside: avoid;
        }

        /* Stick the heading to its following table/list/figure — same job
           the old buildPaginationGroups did, but the browser owns it now. */
        h1 + table, h2 + table, h3 + table, h4 + table,
        h1 + ul, h2 + ul, h3 + ul, h4 + ul,
        h1 + ol, h2 + ol, h3 + ol, h4 + ol,
        h1 + figure, h2 + figure, h3 + figure, h4 + figure,
        h1 + p + table, h2 + p + table, h3 + p + table {
            break-before: avoid;
            page-break-before: avoid;
        }

        figure, img, blockquote, pre {
            break-inside: avoid;
            page-break-inside: avoid;
        }

        /* Preserve all colored backgrounds (highlights, cell tints, bg-color
           marks). Browsers strip these by default in print. */
        *, *::before, *::after {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
        }

        /* Plate cells get inline backgroundColor — make sure it renders. */
        td, th {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
        }

        /* Allow long words / URLs to break instead of overflowing the page. */
        p, li, td, th, blockquote {
            overflow-wrap: break-word;
            word-wrap: break-word;
        }

        /* Trailing empty paragraphs the editor keeps for the caret should
           not be visible in the PDF. */
        p:last-child:empty,
        p[data-slate-node="element"]:last-child:not(:has(*)):not(:has(:not(:empty))) {
            display: none;
        }

        /* Strip editor-only cell selection state. A cell selected when the user
           clicked Export carries the cellSelected class, whose ::after paints a
           blue tint (rgba(35,131,226,.08)) that otherwise bleeds into the PDF.
           Kill ONLY the pseudo-element overlay + box-shadow, never the element's
           own background, so legitimately colored cells keep their fill even if
           they happened to be selected. CSS Modules hash the class name, so we
           match by substring. Also clear the text-selection highlight. */
        [class*="cellSelected"]::after,
        [class*="cellSelected"]::before {
            display: none !important;
            content: none !important;
        }
        [class*="cellSelected"] {
            box-shadow: none !important;
        }
        *::selection {
            background: transparent !important;
        }

        /* Tables: fit the page width while PRESERVING column proportions.
           Plate stores per-column widths in px (sized for the ~1229px editor).
           normalizeTableColumnsForPrint() rewrites those <col> widths as
           percentages on the clone; combined with table-layout: fixed the
           browser then honours the proportions exactly and scales them to the
           page content width instead of auto-sizing columns by content (which
           squished the label column to one-letter-per-line). */
        table {
            max-width: 100% !important;
            width: 100% !important;
            table-layout: fixed !important;
        }

        /* The live editor wraps each table in a horizontal scroll box
           (.tableScroll, RECUR-26175). Here the table is forced to 100% width
           with percentage columns, so it always fits the wrapper — but neutralise
           the scroll box anyway so a print page can never render a clipped table
           or a stray scrollbar. */
        [class*="tableScroll"] {
            overflow: visible !important;
        }

        /* Editor canvas paints a gray "Google-Docs page" background — print
           pages have their own page color. */
        [class*="canvas"],
        [class*="canvasWrap"] {
            background: #ffffff !important;
        }
    }
`;

// Strip interactive chrome that won't matter in the print output. Most of it
// is already hidden by CSS but a belt-and-suspenders DOM clean keeps the
// printed page lean.

export async function exportEditorToPdfPrint({ target, filename = 'document' }: ExportToPdfOptions): Promise<void> {
    // Locate the editor's content root inside `target`. Prefer the Plate
    // testid, fall back to any contenteditable, fall back to target itself.
    const contentEl =
        (target.querySelector('[data-testid="plate-editor-content"]') as HTMLElement | null) ??
        (target.querySelector('[contenteditable]') as HTMLElement | null) ??
        target;

    // Hidden iframe — visible but offscreen wouldn't help here; print() works
    // on truly hidden frames. We pin to 0×0 so layout doesn't reflow.
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.opacity = '0';
    iframe.style.pointerEvents = 'none';
    document.body.appendChild(iframe);

    const cleanup = () => {
        try {
            document.body.removeChild(iframe);
        } catch {
            /* already removed */
        }
    };

    try {
        // The iframe needs a same-origin document; appending it creates a
        // blank about:srcdoc context we can populate.
        const doc = iframe.contentDocument;
        const win = iframe.contentWindow;
        if (!doc || !win) {
            throw new Error('iframe document unavailable');
        }

        const hostStyles = collectHostStyles();

        doc.open();
        doc.write(`<!doctype html>
            <html>
                <head>
                    <meta charset="utf-8" />
                    <title>${filename.replace(/[<>&"]/g, '')}</title>
                    ${hostStyles}
                    <style>${PRINT_CSS}</style>
                    <style>
                        /* Let print engine pick the body width from the @page
                           content area — no fixed pixel width, so tables and
                           paragraphs reflow to fit the A3 portrait page
                           without overflowing horizontally. */
                        body {
                            margin: 0 auto;
                            background: #ffffff;
                        }
                    </style>
                </head>
                <body></body>
            </html>`);
        doc.close();

        const clone = contentEl.cloneNode(true) as HTMLElement;
        stripInteractiveChrome(clone);
        trimTrailingEmptyBlocks(clone);
        normalizeTableColumnsForPrint(clone);
        doc.body.appendChild(clone);

        // Wait for the iframe's stylesheets to settle, then explicitly load the
        // brand fonts in the iframe's own context before printing. Relying on
        // the passive fonts.ready alone is not enough — on a freshly-written
        // iframe it resolves before any .ttf fetch is even requested, so print()
        // races the download and renders a system-font fallback on machines
        // without Gilroy installed locally. ensureFontsLoaded forces the load.
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
        await ensureFontsLoaded(doc);
        await waitForImages(clone);

        // One more frame so layout fully resolves before we trigger print.
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

        // Focus the iframe so print() targets it. Without focus, some
        // browsers print the host document instead.
        win.focus();
        win.print();
    } finally {
        // Give the print dialog ~1s to grab the iframe contents before
        // tearing it down. The dialog runs in a separate browser process,
        // but removing the iframe immediately can cancel the print job
        // in some browsers.
        setTimeout(cleanup, 1500);
    }
}
